import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, Info } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ConnectInstagramButton from "@/components/ConnectInstagramButton";
import PlanFeatureList from "@/components/PlanFeatureList";
import api from "@/lib/api";
import PlanCarousel from "@/components/PlanCarousel";
import YearlySavings from "@/components/YearlySavings";
import TrustStrip from "@/components/TrustStrip";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const tiers = [
  {
    key: "free",
    name: "Free",
    tagline: "Everything you need to try DM selling — no card required.",
    highlight: false,
    cta: "Start free",
    features: [
      "5 Catalog Products",
      "Sell Physical Products & Digital Products (Instant Delivery Link inside DMs and Emails)",
      "Full DM Checkout Automation on Instagram",
      "15 Customer Conversations / Month",
      "UPI, Bank Transfer & QR Code Payments (Manual Confirmation)",
      "Razorpay, Stripe & PayPal (Automatic Confirmation)",
      "Unlimited Payment Links",
      "Automatic Story Replies (Generic Message)",
      "Basic Merchant Dashboard Access",
      "FAQs, Exchange Policy & Return Policy Shown Automatically in DMs",
      "Automatic Return & Exchange Handling",
      "Charge Shipping Fee From Customers at Checkout",
      "Order & Return Email Confirmations for Customers",
      "Shipped Emails (Sent When You Mark An Order Shipped or Book a Courier)",
      "Multiple Global Courier Partners Supported",
      "Schedule Pickup & Reverse Pickup from Merchant Dashboard",
      "New Order Alerts on Merchant Dashboard",
      "Checkout DM Branding Shown on Customer Checkout inside DMs & Emails",
      "Email Support",
    ],
  },
    {
    key: "creator",
    name: "Creator",
    tagline: "For creators & affiliate marketers — sell files, give freebies, or share your affiliate links.",
    highlight: false,
    cta: "Choose Creator",
    features: [
      "150 Digital-Only Catalog Products",
      "Give Free Digital Products Also (Give Away Files or Links)",
      "Sell Digital Products Only (No Physical Products Allowed)",
      "Unlimited Conversations",
      "Comment-to-DM Automation",
      "Category based search inside DMs",
      "Preview / Affiliate Link Button on Any Product inside DMs",
      "Affiliate Marketers Welcome: Show Only Your Affiliate Link in DMs (Keep 100% Commission)",
      "Shareable Public Catalog Page (Storefront) for Your Digital Products",
      "One-Link Checkout — Share it on YouTube, WhatsApp, TikTok, or anywhere; customers can buy without ever DMing you",
      "Checkout DM Branding Inside Emails & Storefront",
      "Buyer Receives the Download Links in the Emails",
      "Merchants get the Customer Name & Emails",
      "Order Dashboard with Order History",
      "Email Support",
    ],
  },
  {
    key: "starter",
    name: "Starter",
    tagline: "For creators selling every day who've outgrown Free.",
    highlight: false,
    cta: "Choose Starter",
    features: [
      "Everything in Free & Creator",
      "500 Catalog Products",
      "Shareable Public Catalog Page with Instant Product Search and 1 Extra Product Photo (2 Total)",
      "Preview / Affiliate Link Button on Any Product inside DMs",
      "Category based search inside DMs",
      "Unlimited Conversations",
      "Catalog Search with 3 Filters/Keywords inside DMs",
      "No Checkout DM Branding, Anywhere",
      "Full Merchant Dashboard",
      "Customer Order History Dashboard",
      "Abandoned Cart Info to Merchant",
      "Take Over Any Chat, Anytime (Useful for Customer Support)", 
      "Automatic Delivery Status Updates (Delivered / RTO / Failed, etc)",
      "Auto Stock Updates on Every Sale, Return or Exchange",
      "Automatic Free Shipping Above a Minimum Cart Value",
      "Reminders for Unshipped Orders",
      "Your Company Address in Emails",
      "Fast Email Support",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    tagline: "Turn commenters into customers and put marketing on autopilot.",
    highlight: true,
    cta: "Choose Growth",
    features: [
      "Everything in Starter",
      "1,000 Catalog Products",
      "Social Links, Active Coupons, 2 More Extra Photo (3 Total) & More on Your Public Catalog Page",
      "Product-Matched Story Replies",
      "Dead Stock Insights",
      "Automatic 'Trending Products' Category Inside DMs",
      "Catalog Search with 4 Filters/Keywords inside DMs",
      "CSV Export of Customers, Orders & Returns",
      "Automatic & Manual Coupons (Applicable Once Per Customer)",
      "Time Based Offers (Auto Expire)",
      "Usage or Count Based Offers (Auto Expire After N Uses)",
      "Automatic Delivery Status Double-Check (Catches Missed Updates)",
      "Multi-Step Automatic Abandoned Cart Recovery",
      "Automatic Low Stock Alerts (Per Size)",
      "Automatically Ask For a Review From Customers After N Days",
      "Add Your Store's Social Media Links (Instagram, Facebook & YouTube) in Emails",
      "Revenue & Conversion Analytics",
      "Priority Support",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "Run Instagram like a full storefront, with a team behind it.",
    highlight: false,
    cta: "Choose Pro",
    features: [
      "Everything in Growth",
      "10,000 Catalog Products",
      "3 Extra Product Photos (4 Total), Search Engine-Indexable Product Pages, Bestseller Products, Your Brand Logo & More on Your Public Catalog Page",
      "3 Team Members (1 Admin + 2 Users)",
      "Extra Team Members Available",
      "Catalog Search with 5 Filters/Keywords inside DMs",
      "Export Your Full Catalog to CSV",
      "Bulk Product Photo Upload (Upto 50 At Once)",
      "Quantity-Based Discounts",
      "Cross-Sell Suggestions In Order & Shipped Emails",
      "Shuffle Catalog Order in DMs to Move Dead Stock",
      "Show Bestseller Products inside DMs to Customers",
      "A 1:1 Quick Onboarding Call with Founder Prince Jindal",
      "Real-Time Webhooks (Orders & Returns)",
      "Advanced Analytics",
      "Show Your Brand's Logo, Website, Phone & much more inside customer emails",
      "Dedicated Support",
    ],
  },
];

const pricingFaqs = [
  { q: "How much does each Checkout DM plan cost?", a: "There are 5 plans. Monthly prices: Free ₹0 ($0), Creator ₹499 ($9), Starter ₹1,499 ($19), Growth ₹2,899 ($39), Pro ₹6,499 ($69). Yearly billing gives 2 months free (pay for 10 months): Creator ₹4,990 ($90), Starter ₹14,990 ($190), Growth ₹28,990 ($390), Pro ₹64,990 ($690). There are no transaction fees on any plan." },
  { q: "Do you charge transaction fees?", a: "No. CheckoutDM never takes any commission from your orders." },
  { q: "Who receives customer payments?", a: "Payments always go directly to your own payment account." },
  { q: "Can I upgrade later?", a: "Yes. Upgrade or downgrade anytime without losing your data." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel anytime from your dashboard — you'll keep full access to your current plan until the billing period you've already paid for ends, with no further charges after that." },
  { q: "Can I get a refund?", a: "Subscription payments are non-refundable, including partial or unused periods. If you cancel, you simply keep using CheckoutDM until your paid period ends. See our Refund Policy for full details." },
  { q: "What happens when I hit the Free plan's 15 conversation limit?", a: "New customer conversations pause until your monthly limit resets, or you upgrade to a paid plan with unlimited conversations." },
];

const pricingFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: pricingFaqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

// ---------------------------------------------------------------------------
// Info tooltip — small (i) button next to a feature label. Tap/click to open,
// tap/click outside to close. Same behavior on mobile and desktop.
// ---------------------------------------------------------------------------
function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [open]);

  return (
    <span className="relative inline-flex" ref={wrapperRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="More info"
        aria-expanded={open}
        className="ml-1.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full text-ink/35 transition-colors hover:text-hotpink focus:outline-none focus-visible:ring-2 focus-visible:ring-hotpink/40"
      >
        <Info size={14} strokeWidth={2.25} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.14 }}
            role="tooltip"
            className="absolute left-1/2 top-full z-50 mt-2 w-60 -translate-x-1/2 rounded-xl bg-ink px-3.5 py-2.5 text-left text-xs font-normal leading-relaxed text-cream shadow-xl"
          >
            <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-ink" />
            {text}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Comparison table data — grouped into sections, each row optionally carrying
// an `info` tooltip and either string values (per plan) or boolean values
// (rendered as check / dash).
// ---------------------------------------------------------------------------
const FEATURE_GROUPS = [
  {
    section: "Core Commerce",
    rows: [
      {
        label: "Catalog Products",
        info: "The maximum number of products you can list for customers to browse and buy inside Instagram DMs.",
        values: ["5", "150", "500", "1,000", "10,000"],
      },
      {
        label: "Digital Products",
        info: "List digital items (courses, files, links, etc.) alongside physical products. Buyers get their delivery link automatically instantly on Instagram DM and email, right after payment — no manual sending.",
        values: [true, true, true, true, true],
      },
      {
        label: "Public Catalog Page",
        info: "A shareable link (checkoutdm.com/store/yourIGhandle) where customers can browse your catalog, add items to a cart, and pay right there on the page — no Instagram DM required. Creator plan gives a rich product page with extended descriptions per every digital item. Starter adds a rich product page (extended description, sizing guide, care instructions, FAQs) with 1 extra photo per product; Growth adds social links, your active coupons, and a 2nd extra photo; Pro adds a 3rd extra photo, your logo, tagline, website, and much more.",
        values: ["—", "Basic Storefront", "+ Address", "+ Social links & coupons", "+ Extra photos & branding"],
      },
      {
        label: "Direct Checkout — No DM Needed",
        info: "Customers can complete an entire purchase — cart, address, discount code, shipping fee, and payment — right on your Public Catalog Page. Share the one link on YouTube, WhatsApp, Telegram, TikTok, Linktree, or anywhere else off Instagram; no DM required to buy.",
        values: [false, true, true, true, true],
      },
      {
        label: "Google-Indexable Product Pages",
        info: "Every paid plan gets a shareable, rich-preview link for each product. Only Pro's individual product pages are also indexable by Google search — Starter and Growth pages stay shareable but won't show up in Google results.",
        values: [false, false, false, false, true],
      },
      {
        label: "Extra Product Photos on Catalog Page",
        info: "Beyond the 1 mandatory main photo, you can add extra photos shown in a gallery on your Public Catalog Page.",
        values: ["—", "—", "1 extra (2 total)", "2 extra (3 total)", "3 extra (4 total)"],
      },
      {
        label: "Extended Description, Sizing Guide, Care Instructions & FAQs",
        info: "Add richer detail to each product's catalog page — a longer description, a sizing guide, washing/care instructions, and optional FAQs — shown to customers browsing before they DM you.",
        values: [false, true, true, true, true],
      },
      {
        label: "Preview / Buy (Affiliate) Link Button",
        info: "Add an optional button next to a product — a 'Preview' link (for example, show your website's link OR public catalog page's individual product link) or a 'Buy' link that sends customers straight to an external/affiliate page instead of checking out in DM.",
        values: [false, true, true, true, true],
      },
      {
        label: "Customer Conversations / Month",
        info: "A conversation is counted once per unique customer who messages your account in a month — not per message.",
        values: ["15", "Unlimited", "Unlimited", "Unlimited", "Unlimited"],
      },
      {
        label: "CheckoutDM Branding",
        info: "On the Free plan, a small 'Powered by CheckoutDM' note appears in customer-facing messages and emails. Creator shows it only in emails and on the storefront. Starter, Growth and Pro remove it everywhere.",
        values: ["Shown", "Only in Emails", "Removed", "Removed", "Removed"],
      },
      {
        label: "Payment Methods",
        info: "Razorpay, Stripe, PayPal, UPI ID, QR code, bank transfer, and shareable payment links — accepted on every plan, at no extra cost.",
        values: [true, true, true, true, true],
      },
      {
        label: "Charge Shipping Fee From Customers",
        info: "Add a shipping fee on top of the product price at checkout — passed on to the customer, not deducted from what you earn. Since Creator plan is only for digital products, you don't charge such fees.",
        values: [true, "—", true, true, true],
      },
      {
        label: "Automatic Free Shipping Above a Minimum Cart Value",
        info: "Set your own minimum cart value — shipping becomes free automatically once a customer crosses it. Applies to every order, with no usage limit, so it keeps encouraging bigger carts instead of being a one-time coupon.",
        values: [false, "—", true, true, true],
      },
    ],
  },
  {
    section: "Automation & Marketing",
    rows: [
      {
        label: "Story Reply Automation",
        info: "Auto-reply when someone responds to your Instagram Story. Free plan sends a generic reply; Growth and Pro match the reply to the exact product in the Story.",
        values: ["Generic", "Generic", "Generic", "Product-matched", "Product-matched"],
      },
      {
        label: "Merchant FAQs Inside DMs",
        info: "Customers can get instant answers to common questions (shipping time, sizing, returns, etc.) without you lifting a finger.",
        values: [true, true, true, true, true],
      },
      {
        label: "Comment-to-DM Automation",
        info: "Automatically DM anyone who comments a keyword on your posts or reels — great for hackathon/giveaway-style promotions.",
        values: [false, true, true, true, true],
      },
      {
        label: "Automatic & Manual Coupons",
        info: "Create discount codes yourself, or let CheckoutDM apply them automatically based on rules you set (first order, cart value, etc.).",
        values: [false, false, false, true, true],
      },
      {
        label: "Time-Based Offers",
        info: "Run limited-time sales that start and end automatically — no manual toggling required.",
        values: [false, false, false, true, true],
      },
      {
        label: "Count-Based Offers",
        info: "Run limited-time sales that end automatically with 100 customers (or whatever value you set) — no manual toggling required.",
        values: [false, false, false, true, true],
      },
      {
        label: "Buy X Get Y% Off (Quantity Discounts)",
        info: "Automatically apply a percentage discount once a customer's cart reaches a set number of items — e.g. Buy 2 Get 10% Off.",
        values: [false, false, false, false, true],
      },
      {
        label: "Cross-Sell Suggestions",
        info: "We show 3 related products in the order confirmation email, and 3 different ones in the shipping email — based on what other customers have bought together. An easy way to turn a one-time buyer into a repeat customer.",
        values: [false, false, false, false, true],
      },
      {
        label: "Abandoned Cart Recovery",
        info: "Starter notifies you when a cart is abandoned so you can follow up yourself. Growth and Pro also send a multi-step automatic recovery sequence to the customer. If a customer says why they hesitated (for example, asking for a discount), the reason shows in your Stuck tab — in full on Growth and Pro, and for a limited number of carts on Starter. Free and Creator don't include abandoned-cart alerts.",
        values: ["—", "—", "Merchant alert only", "Multi-step automatic", "Multi-step automatic"],
      },
      {
        label: "Review Requests After N Days",
        info: "Automatically ask customers to leave a review a set number of days after their order — you choose the delay.",
        values: [false, false, false, true, true],
      },
    ],
  },
  {
    section: "Orders, Stock & Insights",
    rows: [
      {
        label: "Order Dashboard & Customer History",
        info: "See every order and all customer's full purchase history in one place. Free plan has a limited view. Creator plan doesn't show Customer History; only Order History.",
        values: ["Limited", "Limited", "Full", "Full", "Full"],
      },
      {
        label: "Dashboard Order Notifications",
        info: "Get notified inside your dashboard the moment a new order comes in.",
        values: [true, true, true, true, true],
      },
      {
        label: "Manual Chat Takeover",
        info: "Jump into any automated conversation yourself, at any time, without breaking the automation flow. This becomes necessary when your customer wants your help, thus, manual intervention.",
        values: [false, false, true, true, true],
      },
      {
        label: "Automatic Return Handling",
        info: "Customers can request a return directly in DM and CheckoutDM handles the workflow automatically by incrementing the relevant stock and initiating a reverse pickup if you have attached the courier partner.",
        values: [true, "—", true, true, true],
      },
      {
        label: "Automatic Exchange Stock Check",
        info: "When a customer requests an exchange for a different size, CheckoutDM automatically checks whether that size is in stock before approving it, and updates your catalog counts once it's processed — skipped automatically for digital products. Courier pickup of the old item and shipping the replacement still need your action: handled directly from your Shiprocket dashboard, or via your Shippo dashboard's daily pickup schedule on Shippo.",
        values: [false, "—", true, true, true],
      },
      {
        label: "Delivery Status Tracking (Delivered / RTO / Failed)",
        info: "Automatically updates each order's status on your Orders tab the moment Shiprocket confirms it's Delivered, Returned to origin, or failed delivery — instead of a static 'Shipped'. This is also what lets CheckoutDM automatically schedule a reverse pickup the moment a return is approved on an order that's actually been delivered. Starter gets real-time updates via Shiprocket's webhook; Growth and Pro add an automatic re-check for any order shipped a while ago, in case a webhook update is ever missed.",
        values: ["—", "—", "Real-time (webhook)", "Real-time + auto-recheck", "Real-time + auto-recheck"],
      },
      {
        label: "Auto Stock Updates",
        info: "Your catalog's stock count updates automatically after every sale and every processed return — no manual editing.",
        values: [false, "—", true, true, true],
      },
      {
        label: "Order & Return Email Confirmations",
        info: "Customers automatically receive an email confirming their order or return — no extra tools needed.",
        values: [true, true, true, true, true],
      },
      {
        label: "Order Shipped Emails",
        info: "Customers are emailed automatically once you mark an order as shipped. This is necessary from merchant's side for correct behavior of Return workflow.",
        values: [true, "—", true, true, true],
      },
      {
        label: "Catalog Search Filters",
        info: "The number of filters (e.g. price, size, category) customers can use to search your catalog inside DMs. Customers can type 'men black slim jeans under 2000'",
        values: ["—", "—", "3", "4", "5"],
      },
      {
        label: "Dead Stock Insights",
        info: "See which products aren't selling so you know what to discount, bundle, or discontinue.",
        values: [false, false, false, true, true],
      },
      {
        label: "Trending Products Category (DM Browsing)",
        info: "Adds an automatic 'Trending' category customers can browse when they reply CATEGORIES in DM — ranked by your last 30 days of sales and recalculated automatically. Different from the Bestseller pin below: this one is computed for you from real sales data, no tagging needed.",
        values: [false, false, false, true, true],
      },
      {
        label: "Catalog Shuffle for Dead Stock",
        info: "Shuffles the order products appear in when a customer browses your catalog in DM, so older or less-viewed items get seen too — an easy way to move stock that's been sitting. Bestsellers and new arrivals still show first for every customer.",
        values: [false, false, false, false, true],
      },
      {
        label: "CSV Export",
        info: "Download your data about customers, orders, and returns as CSV files for your own records or external tools.",
        values: [false, false, false, true, true],
      },
      {
        label: "Catalog CSV Export",
        info: "Download your entire product catalog — including stock per size — as a CSV file. Useful for backups or managing a large catalog in a spreadsheet.",
        values: [false, false, false, false, true],
      },
      {
        label: "Bulk Product Photo Upload",
        info: "Upload up to 50 product photos in a single batch instead of one at a time — built for stocking a large catalog quickly.",
        values: [false, false, false, false, true],
      },
      {
        label: "Automatic Low Stock Alerts",
        info: "Get notified before a product runs out, so you never undersell and fill your inventory on time. Get alerts per size per product. Set a number and forget.",
        values: [false, false, false, true, true],
      },
      {
        label: "Analytics",
        info: "Growth includes revenue and conversion analytics. Pro adds deeper, more advanced reporting.",
        values: ["—", "—", "—", "Revenue & conversion", "Advanced"],
      },
      {
        label: "Company Address in Email Footer",
        info: "Your business address is automatically added to the footer of order and return emails — required for legal compliance in many regions and builds trust with customers.",
        values: [false, false, true, true, true],
      },
      {
        label: "Social links in emails",
        info: "Growth and Pro merchants can add Instagram, Facebook, and YouTube links — shown as clickable icons in the footer of every customer email to create awareness about your other social media channels too.",
        values: ["—", "—", "—", true, true],
      },
      {
        label: "Brand Details in Emails",
        info: "Pro merchants can add their brand logo, business registration number, website link, tag line, and phone number to every customer-facing email — order placed, order shipped, return, and feedback request emails.",
        values: ["—", "—", "—", "—", true],
      },
      {
        label: "Webhooks",
        info: "Push order and return events to your own CRM, ERP, or automation stack in real time — available on Pro only.",
        values: ["—", "—", "—", "—", "Webhooks"],
      },
    ],
  },
  {
    section: "Team & Support",
    rows: [
      {
        label: "Team Members",
        info: "How many people (including you) can log in and manage the account. Pro allows adding extra seats (on extra payment) beyond the included 3.",
        values: ["1", "1", "1", "1", "3 (+ extra available)"],
      },
      {
        label: "Founder Onboarding Call",
        info: "A short 1:1 onboarding call with CheckoutDM's founder, Prince Jindal, to help you get set up right.",
        values: [false, false, false, false, true],
      },
      {
        label: "Support",
        info: "How quickly and through what channel you can expect help. All plans include email support.",
        values: ["Email", "Fast Email", "Fast Email", "Priority", "Dedicated"],
      },
    ],
  },
];

function ValueCell({ value }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check size={16} className="mx-auto text-hotpink" />
    ) : (
      <span className="text-ink/25">—</span>
    );
  }
  return <span>{value}</span>;
}

export default function Pricing() {
  const [currency, setCurrency] = useState("INR");
  const [yearly, setYearly] = useState(false);
  const [plans, setPlans] = useState(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  useEffect(() => {
    api
      .get("/config")
      .then(({ data }) => setPlans(data.plans))
      .catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Checkout DM Pricing: Plans from ₹0 to ₹6,499/month"
        description="CheckoutDM (Checkout DM) pricing: Free, Creator ₹499, Starter ₹1,499, Growth ₹2,899, Pro ₹6,499 a month. 2 months free on yearly plans. No transaction fees."
        path="/pricing"
        structuredData={pricingFaqSchema}
      />
      <Navbar />

      <section className="px-6 pt-16 pb-10 md:px-12 md:pt-24">
        <div className="mx-auto max-w-6xl text-center md:text-left">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
            Pricing
          </p>
          <h1
            data-testid="pricing-title"
            className="mt-3 font-display text-5xl leading-[0.95] tracking-tighter md:text-7xl"
          >
            Checkout DM Pricing: <span className="text-sunset">pay for the DMs that convert.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-ink/70">
            Every plan gets full DM checkout automation from day one — no
            feature gates on the basics, no commission on your sales. Upgrade
            only when you need more scale, more automation, or more hands on deck.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-ink/5 px-4 py-1.5 text-xs font-semibold text-ink/60">
            <Info size={13} /> This pricing is for merchants — your buyers never pay CheckoutDM anything.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <div className="inline-flex items-center gap-1 rounded-full border border-ink/10 bg-white/80 p-1 backdrop-blur">
              {["INR", "USD"].map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  data-testid={`toggle-${c.toLowerCase()}`}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                    currency === c
                      ? "bg-ink text-cream"
                      : "text-ink/60 hover:text-ink"
                  }`}
                >
                  {c === "INR" ? "₹ INR" : "$ USD"}
                </button>
              ))}
            </div>

            <div className="relative inline-flex rounded-full border border-ink/10 bg-white p-1 shadow-sm">
              <button
                onClick={() => setYearly(false)}
                className={`relative z-0 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${
                  !yearly ? "text-white" : "text-ink/60"
                }`}
              >
                {!yearly && (
                  <motion.span
                    layoutId="pricing-billing-toggle-pill"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    className="absolute inset-0 -z-10 rounded-full bg-hotpink"
                  />
                )}
                Monthly
              </button>
              <button
                onClick={() => setYearly(true)}
                className={`relative z-0 flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${
                  yearly ? "text-white" : "text-ink/60"
                }`}
              >
                {yearly && (
                  <motion.span
                    layoutId="pricing-billing-toggle-pill"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    className="absolute inset-0 -z-10 rounded-full bg-hotpink"
                  />
                )}
                Yearly
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    yearly ? "bg-white/20 text-white" : "bg-green-100 text-green-700"
                  }`}
                >
                  Save 2 Months
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <TrustStrip className="mb-8" />
          <PlanCarousel labels={tiers.map((t) => t.name)} initialIndex={3}>
          {tiers.map((t, i) => {
            const planData = plans?.[t.key];
            const price =
              currency === "INR"
                ? (yearly ? planData?.price_inr_yearly : planData?.price_inr) ?? 0
                : (yearly ? planData?.price_usd_yearly : planData?.price_usd) ?? 0;

            return (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                data-testid={`pricing-card-${t.name.toLowerCase()}`}
                className={`relative flex w-[82%] shrink-0 snap-center flex-col rounded-[2rem] border p-7 shadow-sm sm:w-[58%] md:w-[340px] lg:w-[360px] ${
                  t.highlight
                    ? "border-transparent bg-sunset-gradient text-white shadow-[0_25px_60px_-20px_rgba(255,51,102,0.55)]"
                    : "border-ink/10 bg-white text-ink"
                }`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-hotpink shadow">
                    <Sparkles size={12} /> Recommended
                  </div>
                )}
                {t.key === "pro" && (
                  <div className="absolute -top-3 left-6 inline-flex items-center rounded-full bg-ink px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-cream shadow">
                    For brands &amp; teams
                  </div>
                )}
                <p className="font-display text-2xl font-black tracking-tight">
                  {t.name}
                </p>
                <p className={`mt-2 text-sm ${t.highlight ? "text-white/85" : "text-ink/60"}`}>
                  {t.tagline}
                </p>

                <div className="mt-8 text-center">
                  {plans === null ? (
                    <div
                      className={`mx-auto h-12 w-32 animate-pulse rounded-lg ${
                        t.highlight ? "bg-white/20" : "bg-ink/10"
                      }`}
                    />
                  ) : (
                    <p className="font-display text-5xl font-black tracking-tighter">
                      {currency === "INR"
                        ? `₹${Number(price || 0).toLocaleString("en-IN")}`
                        : `$${Number(price || 0)}`}
                    </p>
                  )}
                  <p className={`mt-1 text-xs ${t.highlight ? "text-white/80" : "text-ink/60"}`}>
                    {t.key === "free"
                      ? "Forever Free"
                      : yearly
                      ? "Billed yearly (Pay for 10 months)"
                      : "Billed monthly"}
                  </p>
                  {yearly && t.key !== "free" && (
                    <>
                      <p className={`mt-1 text-xs font-semibold ${t.highlight ? "text-white" : "text-hotpink"}`}>
                        2 Months Free
                      </p>
                      <YearlySavings
                        monthly={currency === "INR" ? planData?.price_inr : planData?.price_usd}
                        yearly={currency === "INR" ? planData?.price_inr_yearly : planData?.price_usd_yearly}
                        currency={currency}
                        highlight={t.highlight}
                      />
                    </>
                  )}
                </div>

                <PlanFeatureList
                  features={t.features}
                  expanded={showAllFeatures}
                  onToggle={() => setShowAllFeatures((v) => !v)}
                  highlight={t.highlight}
                  testId={`pricing-showmore-${t.key}`}
                />

                <div className="mt-auto pt-8">
                  <ConnectInstagramButton
                    size="md"
                    label={t.cta}
                    testId={`pricing-cta-${t.name.toLowerCase()}`}
                    className={t.highlight ? "bg-white !text-ink w-full" : "w-full"}
                  />
                  <p
                    className={`mt-3 text-center text-xs ${t.highlight ? "text-white/80" : "text-ink/50"}`}
                  >
                    {t.key === "free"
                      ? "No card required · Upgrade anytime"
                      : "No transaction fees · Cancel anytime"}
                  </p>
                </div>
              </motion.div>
            );
          })}
          </PlanCarousel>
        </div>

        <div className="mx-auto mt-14 max-w-3xl rounded-3xl border border-ink/10 bg-white p-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
            Fair usage
          </p>
          <p className="mt-3 font-display text-2xl font-black tracking-tight">
            No transaction fees on checkouts — you only pay your plan.
          </p>
                    <p className="mt-2 text-sm text-ink/60">
            Payment processor fees (UPI/card gateway) apply as pass-through.
          </p>
          <p className="mt-2 text-sm text-ink/60">
            Subscriptions are non-refundable. Cancel anytime and keep access until your paid period ends. See our{" "}
            <Link to="/refund-policy" className="underline">
              Refund Policy
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center font-display text-4xl font-black tracking-tight">
            Compare Plans
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-ink/60">
            Every row has a plain-English explanation — tap the{" "}
            <Info size={13} className="inline -translate-y-px text-ink/40" />{" "}
            next to anything you're not sure about.
          </p>

          <div className="mt-10 overflow-x-auto">
            <div className="min-w-[820px] overflow-x-hidden rounded-[2rem] bg-white shadow">
              <table className="w-full border-collapse [&_tbody_tr:nth-child(odd)]:bg-cream/50 [&_td:nth-child(5)]:bg-hotpink/5 [&_th:nth-child(5)]:bg-hotpink">
                <thead>
                  <tr className="bg-ink text-white">
                    <th className="p-5 text-left">Feature</th>
                    <th className="p-5 text-center">Free</th>
                    <th className="p-5 text-center">Creator</th>
                    <th className="p-5 text-center">Starter</th>
                    <th className="p-5 text-center bg-hotpink">Growth</th>
                    <th className="p-5 text-center">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_GROUPS.map((group) => (
                    <>
                      <tr key={`section-${group.section}`} className="bg-ink/[0.04]">
                        <td
                          colSpan={6}
                          className="px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-ink/50"
                        >
                          {group.section}
                        </td>
                      </tr>
                      {group.rows.map((row) => (
                        <tr key={row.label} className="border-b">
                          <td className="p-4">
                            <span className="inline-flex items-center">
                              {row.label}
                              {row.info && <InfoTooltip text={row.info} />}
                            </span>
                          </td>
                          {row.values.map((v, idx) => (
                            <td key={idx} className="text-center">
                              <ValueCell value={v} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24 md:px-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center font-display text-4xl font-black">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="mt-10 text-base">
            {pricingFaqs.map((f, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`}>
                <AccordionTrigger className="text-left font-display text-lg font-bold">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-ink/70">
                  {f.q === "Can I get a refund?" ? (
                    <>
                      Subscription payments are non-refundable, including
                      partial or unused periods. If you cancel, you simply
                      keep using CheckoutDM until your paid period ends. See
                      our{" "}
                      <Link to="/refund-policy" className="underline">
                        Refund Policy
                      </Link>{" "}
                      for full details.
                    </>
                  ) : (
                    f.a
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
    </>
  );
}
